import { FC, memo } from "react";
import { styled } from "@mui/material";
import { featuredRecording, recordings } from "../../utils/landing-data";
import { bg, bp, cardBg, fluid, font, w } from "../../styles/landing-tokens";
import { Container, DisplayHeading } from "./shared";
import { MicIcon, PlayIcon } from "./icons";
import { useReveal } from "./reveal";

type Recording = (typeof recordings)[number];

const Section = styled("section")({
  background: bg,
  /**
   * Figma: publications end at y=5698, this heading sits at y=6032 (334 gap).
   * Trimmed by 13px because the publication list renders 13px taller than
   * Figma — the browser breaks those long titles a hair differently.
   */
  paddingTop: 321,
  paddingBottom: 0,

  [bp.md]: { paddingTop: 140, paddingBottom: 0 },
});

/** Figma places the heading at x=211, indented from the 188px content edge. */
const Heading = styled(DisplayHeading)({
  marginLeft: 23,

  [bp.md]: { marginLeft: 0 },
});

/* ------------------------------------------------------------------ *
 * Featured podcast banner — Figma FRAME "Link" 1544×395
 * ------------------------------------------------------------------ */

const Featured = styled("div")({
  position: "relative",
  marginTop: 65,
  padding: "71px 0 0 71px",
  minHeight: 395,
  borderRadius: 20.1,
  border: `0.7px solid ${w(0.08)}`,
  background: "linear-gradient(135deg, #060B14 0%, #0C1320 50%, #0A1018 100%)",
  overflow: "hidden",

  [bp.md]: { padding: "40px 24px", minHeight: 0 },
});

/** Soft white radial bloom on the right half of the banner. */
const Bloom = styled("div")({
  position: "absolute",
  top: -116,
  right: -100,
  width: 627,
  height: 627,
  borderRadius: "50%",
  background: `radial-gradient(circle, ${w(0.18)} 0%, rgba(0,0,0,0) 60%)`,
  filter: "blur(150px)",
  opacity: 0.5,
  pointerEvents: "none",
});

const FeaturedInner = styled("div")({
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "flex-start",
  gap: 50,

  [bp.sm]: { flexDirection: "column", gap: 24 },
});

/** 120px white play disc with its own glow ring and outer blur. */
const PlayDisc = styled("a")({
  position: "relative",
  flexShrink: 0,
  width: 120,
  height: 120,
  marginTop: 66,
  borderRadius: "50%",
  border: "none",
  background: "#FFFFFF",
  boxShadow: `0 0 75px ${w(0.4)}`,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform .2s ease",

  "&:hover": { transform: "scale(1.05)" },

  [bp.sm]: { width: 80, height: 80, marginTop: 0 },
});

const FeaturedBody = styled("div")({
  flex: 1,
  minWidth: 0,
});

const MetaRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 20,
  flexWrap: "wrap",
});

const Badge = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  padding: "5px 13px",
  borderRadius: 10,
  background: "#FFFFFF",
  fontFamily: font.sans,
  fontWeight: 700,
  fontSize: 11.3,
  lineHeight: "16.9px",
  letterSpacing: "0.17em",
  textTransform: "uppercase",
  color: bg,
});

const MetaText = styled("span")<{ strength?: number }>(({ strength = 0.55 }) => ({
  fontFamily: font.sans,
  fontWeight: strength > 0.4 ? 600 : 500,
  fontSize: 13.8,
  lineHeight: "20.7px",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: w(strength),
}));

const Dot = styled("span")<{ strength?: number }>(({ strength = 0.3 }) => ({
  width: 5,
  height: 5,
  borderRadius: "50%",
  background: w(strength),
  flexShrink: 0,
}));

const FeaturedTitle = styled("h3")({
  /** Figma: meta row ends at y=6430, title starts at y=6450. */
  margin: "20px 0 0",
  maxWidth: 782,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: fluid(26, 42.6),
  lineHeight: 1.15,
  letterSpacing: "-0.029em",
  color: "#FFFFFF",
});

const StatsRow = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 20,
  marginTop: 27,
  flexWrap: "wrap",
});

const StatText = styled("span")({
  display: "inline-flex",
  alignItems: "center",
  gap: 9,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 15,
  lineHeight: "22.6px",
  color: w(0.45),
});

const Tags = styled("div")({
  display: "flex",
  gap: 10,
  marginTop: 30,
  flexWrap: "wrap",
});

const Tag = styled("span")({
  padding: "5px 14px",
  borderRadius: 10,
  border: `0.7px solid ${w(0.08)}`,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 12.5,
  lineHeight: "18.8px",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  color: w(0.5),
});

/** 18-bar audio waveform pinned to the banner's right edge. */
const WAVEFORM = [13, 35, 9, 31, 14, 52, 18, 17, 19, 23, 17, 39, 17, 8, 44, 35, 20, 12];

const Waveform = styled("div")({
  position: "absolute",
  right: 60,
  top: 170,
  display: "flex",
  alignItems: "center",
  gap: 4.5,
  height: 55,

  [bp.md]: { display: "none" },
});

const Bar = styled("span")<{ h: number }>(({ h }) => ({
  display: "block",
  width: 3,
  height: h,
  borderRadius: 2,
  background: "#FFFFFF",
  transformOrigin: "center",
  animation: "barPulse 1.5s ease-in-out infinite",
}));

/* ------------------------------------------------------------------ *
 * Recording cards — Figma FRAME "RecordingCard" 497×606, 3-up grid
 * ------------------------------------------------------------------ */

const CardGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  /** Figma: 522px column pitch (497 + 25), 640px row pitch (606 + 34). */
  columnGap: 25,
  rowGap: 34,
  marginTop: 65,

  [bp.md]: { gridTemplateColumns: "repeat(2, 1fr)" },
  [bp.sm]: { gridTemplateColumns: "1fr" },
});

const Card = styled("article")({
  display: "flex",
  flexDirection: "column",
  minHeight: 606,
  borderRadius: 20,
  border: `0.7px solid ${w(0.06)}`,
  background: "rgba(12, 19, 32, 0.55)",
  overflow: "hidden",
});

/** 496×279 cover with the gradient wash, badge, duration chip and play disc. */
const Cover = styled("div")({
  position: "relative",
  aspectRatio: "496 / 279",
  background: `linear-gradient(135deg, rgba(57,202,136,0.1) 0%, ${cardBg} 100%)`,
  overflow: "hidden",
});

const CoverImage = styled("img")({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  filter: "brightness(0.8)",
});

const CoverPlay = styled("a")({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 70,
  height: 70,
  borderRadius: "50%",
  border: "none",
  background: "#FFFFFF",
  boxShadow: `0 0 38px rgba(57, 202, 136, 0.4)`,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform .2s ease",

  "&:hover": { transform: "translate(-50%, -50%) scale(1.08)" },
});

const RecordingBadge = styled("span")({
  position: "absolute",
  top: 16,
  left: 16,
  padding: "3px 10px",
  borderRadius: 5,
  background: "#000000",
  border: `0.7px solid ${w(0.1)}`,
  fontFamily: font.sans,
  fontWeight: 600,
  fontSize: 11.9,
  lineHeight: "17.8px",
  letterSpacing: "0.17em",
  textTransform: "uppercase",
  color: w(0.85),
});

const DurationChip = styled("span")({
  position: "absolute",
  right: 16,
  bottom: 14,
  padding: "2px 10px",
  borderRadius: 5,
  background: "#000000",
  fontFamily: font.mono,
  fontWeight: 400,
  fontSize: 13.1,
  lineHeight: "19.7px",
  color: "#FFFFFF",
});

const CardBody = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  padding: "43px 25px 25px",
});

const CardTitle = styled("h4")({
  margin: 0,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 19.4,
  lineHeight: "26.2px",
  letterSpacing: "-0.019em",
  color: "#FFFFFF",
});

const CardText = styled("p")({
  margin: "22px 0 0",
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: 15.6,
  lineHeight: "25.8px",
  color: w(0.55),
});

const Speaker = styled("div")({
  marginTop: "auto",
  paddingTop: 24,
  fontFamily: font.sans,
  fontWeight: 400,
  fontSize: 13.8,
  lineHeight: "20.7px",
  color: w(0.45),
});

const CardFooter = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  marginTop: 21,
  paddingTop: 15,
  borderTop: `1.25px solid ${w(0.05)}`,
});

const FooterMeta = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 7.5,
  fontFamily: font.sans,
  fontWeight: 500,
  fontSize: 13.1,
  lineHeight: "19.7px",
  color: w(0.4),
});

const FooterLinks = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
  whiteSpace: "nowrap",
});

const FooterLink = styled("a")<{ strong?: boolean }>(({ strong }) => ({
  fontFamily: font.sans,
  fontWeight: strong ? 600 : 400,
  fontSize: 13.1,
  lineHeight: "19.7px",
  color: strong ? "#FFFFFF" : w(0.45),
  textDecoration: "none",
  cursor: "pointer",

  "&:hover": { color: "#FFFFFF" },
}));

/** A recording card that eases up into view, staggered across the grid. */
const RecordingCard: FC<{ item: Recording; index: number }> = ({ item, index }) => {
  const { ref, style } = useReveal({ variant: "scale", delay: (index % 3) * 90 });

  return (
    <Card ref={ref} style={style}>
      <Cover>
        <CoverImage src={item.image} alt="" loading="lazy" />
        <RecordingBadge>Recording</RecordingBadge>
        <CoverPlay
          href={item.link}
          target="_blank"
          rel="noreferrer"
          aria-label={`Watch: ${item.title}`}
        >
          <PlayIcon size={25} />
        </CoverPlay>
        <DurationChip>{item.duration}</DurationChip>
      </Cover>

      <CardBody>
        <CardTitle>{item.title}</CardTitle>
        <CardText>{item.description}</CardText>
        <Speaker>{item.speaker}</Speaker>

        <CardFooter>
          <FooterMeta>
            <span>{item.date}</span>
          </FooterMeta>

          <FooterLinks>
            <FooterLink href={item.link} target="_blank" rel="noreferrer" strong>
              Watch Recording
            </FooterLink>
          </FooterLinks>
        </CardFooter>
      </CardBody>
    </Card>
  );
};

export const Recordings: FC = memo(() => {
  const heading = useReveal();
  const featured = useReveal({ delay: 80 });

  return (
    <Section id="interviews">
    <Container>
      <Heading ref={heading.ref} style={heading.style}>
        {"Conversations on\nmarkets, AI,\nand what comes next."}
      </Heading>

      <Featured ref={featured.ref} style={featured.style}>
        <Bloom />
        <Waveform>
          {WAVEFORM.map((h, i) => (
            <Bar key={i} h={h} style={{ animationDelay: `${i * 0.09}s` }} />
          ))}
        </Waveform>

        <FeaturedInner>
          <PlayDisc
            href={featuredRecording.link}
            target="_blank"
            rel="noreferrer"
            aria-label={`Watch: ${featuredRecording.title}`}
          >
            <PlayIcon size={45} />
          </PlayDisc>

          <FeaturedBody>
            <MetaRow>
              <Badge>{featuredRecording.badge}</Badge>
              <MetaText strength={0.55}>{featuredRecording.show}</MetaText>
              <Dot />
              <MetaText strength={0.35}>{featuredRecording.date}</MetaText>
            </MetaRow>

            <FeaturedTitle>{featuredRecording.title}</FeaturedTitle>

            <StatsRow>
              <StatText>
                <MicIcon /> {featuredRecording.host}
              </StatText>
              <Dot strength={0.2} />
              <StatText>{featuredRecording.duration}</StatText>
              <Dot strength={0.2} />
              <StatText>{featuredRecording.listens}</StatText>
            </StatsRow>

            <Tags>
              {featuredRecording.tags.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </Tags>
          </FeaturedBody>
        </FeaturedInner>
      </Featured>

      <CardGrid>
        {recordings.map((r, i) => (
          <RecordingCard key={i} item={r} index={i} />
        ))}
      </CardGrid>
    </Container>
    </Section>
  );
});
